// Firestore service functions for recipes
import { db } from '../config/firebase.js';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy 
} from 'firebase/firestore';

// Get default recipes (public, read-only)
export const getDefaultRecipes = async () => {
  try {
    const recipesSnapshot = await getDocs(
      query(collection(db, 'recipes'), where('isDefault', '==', true))
    );
    const recipes = {};
    
    recipesSnapshot.forEach((doc) => {
      recipes[doc.id] = {
        id: doc.id,
        ...doc.data()
      };
    });
    
    return recipes;
  } catch (error) {
    console.error('Error fetching default recipes:', error);
    throw error;
  }
};

// Get user-specific recipes from Firestore
export const getUserRecipes = async () => {
  try {
    // Import auth here to avoid circular dependencies
    const { auth } = await import('../config/firebase.js');
    
    // Check if user is authenticated
    if (!auth.currentUser) {
      throw new Error('User not authenticated. Please sign in to access your recipes.');
    }
    
    const recipesSnapshot = await getDocs(
      query(
        collection(db, 'recipes'), 
        where('userId', '==', auth.currentUser.uid)
      )
    );
    const recipes = {};
    
    recipesSnapshot.forEach((doc) => {
      const data = doc.data();
      // Ensure userId exists, is not empty, and matches current user
      if (data.userId && 
          data.userId.trim() !== '' && 
          data.userId === auth.currentUser.uid) {
        recipes[doc.id] = {
          id: doc.id,
          ...data
        };
      }
    });
    
    return recipes;
  } catch (error) {
    console.error('Error fetching user recipes:', error);
    throw error;
  }
};

// Get all recipes (for backward compatibility)
export const getAllRecipes = async (useUserRecipes = false) => {
  if (useUserRecipes) {
    return await getUserRecipes();
  } else {
    return await getDefaultRecipes();
  }
};

// Get a single recipe by ID (checks both default and user recipes)
export const getRecipeById = async (recipeId) => {
  try {
    const recipeDoc = await getDoc(doc(db, 'recipes', recipeId));
    
    if (recipeDoc.exists()) {
      const recipeData = recipeDoc.data();
      const { auth } = await import('../config/firebase.js');
      
      // Allow access to default recipes or user's own recipes
      if (recipeData.isDefault || 
          (auth.currentUser && recipeData.userId === auth.currentUser.uid)) {
        return {
          id: recipeDoc.id,
          ...recipeData
        };
      } else {
        throw new Error('Access denied. You can only view your own recipes.');
      }
    } else {
      console.warn(`Recipe with ID ${recipeId} not found`);
      return null;
    }
  } catch (error) {
    console.error('Error fetching recipe:', error);
    throw error;
  }
};

// Search recipes by title
export const searchRecipes = async (searchTerm, useUserRecipes = false) => {
  try {
    let recipesQuery;
    
    if (useUserRecipes) {
      const { auth } = await import('../config/firebase.js');
      if (!auth.currentUser) {
        throw new Error('User not authenticated. Please sign in to search your recipes.');
      }
      
      recipesQuery = query(
        collection(db, 'recipes'), 
        where('userId', '==', auth.currentUser.uid)
      );
    } else {
      recipesQuery = query(
        collection(db, 'recipes'), 
        where('isDefault', '==', true)
      );
    }
    
    const recipesSnapshot = await getDocs(recipesQuery);
    const recipes = [];
    
    recipesSnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Check if title matches search term
      if (data.title && data.title.toLowerCase().includes(searchTerm.toLowerCase())) {
        // Additional validation for user recipes
        if (useUserRecipes) {
          // Get auth again for validation
          const { auth: authValidation } = require('../config/firebase.js');
          if (data.userId && 
              data.userId.trim() !== '' && 
              authValidation.currentUser &&
              data.userId === authValidation.currentUser.uid) {
            recipes.push({
              id: doc.id,
              ...data
            });
          }
        } else if (data.isDefault === true) {
          recipes.push({
            id: doc.id,
            ...data
          });
        }
      }
    });
    
    return recipes;
  } catch (error) {
    console.error('Error searching recipes:', error);
    throw error;
  }
};

// Get recipes by meal type
export const getRecipesByMeal = async (mealType, useUserRecipes = false) => {
  try {
    let recipesQuery;
    
    if (useUserRecipes) {
      const { auth } = await import('../config/firebase.js');
      if (!auth.currentUser) {
        throw new Error('User not authenticated. Please sign in to access your recipes.');
      }
      
      recipesQuery = query(
        collection(db, 'recipes'), 
        where('userId', '==', auth.currentUser.uid),
        where('meal', '==', mealType)
      );
    } else {
      recipesQuery = query(
        collection(db, 'recipes'), 
        where('isDefault', '==', true),
        where('meal', '==', mealType)
      );
    }
    
    const recipesSnapshot = await getDocs(recipesQuery);
    const recipes = [];
    
    recipesSnapshot.forEach((doc) => {
      const data = doc.data();
      
      // For user recipes, double-check userId exists, is not empty, and matches
      if (useUserRecipes) {
        // Get auth again for validation (auth is available in the parent scope for user recipes)
        const { auth: authValidation } = require('../config/firebase.js');
        if (data.userId && 
            data.userId.trim() !== '' && 
            authValidation.currentUser &&
            data.userId === authValidation.currentUser.uid) {
          recipes.push({
            id: doc.id,
            ...data
          });
        }
      } else {
        // For default recipes, ensure they're marked as default
        if (data.isDefault === true) {
          recipes.push({
            id: doc.id,
            ...data
          });
        }
      }
    });
    
    return recipes;
  } catch (error) {
    console.error(`Error fetching ${mealType} recipes:`, error);
    throw error;
  }
};

// Add a new recipe (user-specific)
export const addRecipe = async (recipeData) => {
  try {
    const { auth } = await import('../config/firebase.js');
    
    if (!auth.currentUser) {
      throw new Error('User not authenticated. Please sign in to add recipes.');
    }
    
    const docRef = await addDoc(collection(db, 'recipes'), {
      ...recipeData,
      userId: auth.currentUser.uid,
      isDefault: false,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    console.log('Recipe added with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error adding recipe:', error);
    throw error;
  }
};

// Update an existing recipe
export const updateRecipe = async (recipeId, updates) => {
  try {
    await updateDoc(doc(db, 'recipes', recipeId), {
      ...updates,
      updatedAt: new Date()
    });
    
    console.log('Recipe updated successfully');
  } catch (error) {
    console.error('Error updating recipe:', error);
    throw error;
  }
};

// Delete a recipe
export const deleteRecipe = async (recipeId) => {
  try {
    await deleteDoc(doc(db, 'recipes', recipeId));
    console.log('Recipe deleted successfully');
  } catch (error) {
    console.error('Error deleting recipe:', error);
    throw error;
  }
};

// Import recipe from NYT Cooking
export const importNYTRecipe = async (url) => {
  try {
    const { auth } = await import('../config/firebase.js');
    
    if (!auth.currentUser) {
      throw new Error('User not authenticated. Please sign in to import recipes.');
    }

    // Validate URL
    if (!url || !url.includes('cooking.nytimes.com')) {
      throw new Error('Please provide a valid NYT Cooking recipe URL');
    }

    // Use a CORS proxy to fetch the recipe page
    // Note: In production, you should use your own backend or a paid proxy service
    const proxyUrl = 'https://api.allorigins.win/raw?url=';
    const response = await fetch(proxyUrl + encodeURIComponent(url));
    
    if (!response.ok) {
      throw new Error('Failed to fetch recipe. Please check the URL and try again.');
    }

    const html = await response.text();
    
    // Parse the HTML to extract recipe data
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Extract JSON-LD structured data (NYT uses this format)
    const scriptTags = doc.querySelectorAll('script[type="application/ld+json"]');
    let recipeData = null;
    
    // Search through all JSON-LD scripts to find the recipe
    for (let scriptTag of scriptTags) {
      try {
        const jsonData = JSON.parse(scriptTag.textContent);
        // Handle array or single object
        const candidate = Array.isArray(jsonData) 
          ? jsonData.find(item => item['@type'] === 'Recipe')
          : jsonData['@type'] === 'Recipe' ? jsonData : null;
        
        if (candidate) {
          recipeData = candidate;
          break;
        }
      } catch (e) {
        continue; // Skip invalid JSON
      }
    }
    
    if (!recipeData) {
      throw new Error('Could not find recipe data on this page. Make sure it\'s a recipe URL.');
    }

    // Extract nutrition information
    const nutrition = recipeData.nutrition || {};
    const caloriesValue = nutrition.calories || 0;
    const calories = typeof caloriesValue === 'number' 
      ? caloriesValue 
      : parseInt(String(caloriesValue).replace(/\D/g, '') || '0');
    
    const proteinValue = nutrition.proteinContent || 0;
    const protein = typeof proteinValue === 'number'
      ? proteinValue
      : parseFloat(String(proteinValue).replace(/[^\d.]/g, '') || '0');

    // Extract and format ingredients
    const ingredients = (recipeData.recipeIngredient || []).map(ingredientText => {
      // Basic parsing - extract quantity, unit, and name
      const match = ingredientText.match(/^([\d\/\s.]+)?\s*([a-zA-Z]+)?\s*(.+)$/);
      
      return {
        name: match ? match[3].trim() : ingredientText,
        quantity: match ? match[1]?.trim() || '1' : '1',
        unit: match ? match[2]?.trim() || '' : '',
        calories: 0, // NYT doesn't provide per-ingredient nutrition
        protein: 0
      };
    });

    // Determine meal type based on recipe category or default to dinner
    let mealType = 'dinner';
    const categories = recipeData.recipeCategory || [];
    const categoryStr = Array.isArray(categories) ? categories.join(' ').toLowerCase() : '';
    
    if (categoryStr.includes('breakfast') || categoryStr.includes('brunch')) {
      mealType = 'breakfast';
    } else if (categoryStr.includes('lunch')) {
      mealType = 'lunch';
    } else if (categoryStr.includes('snack') || categoryStr.includes('appetizer')) {
      mealType = 'snack';
    }

    // Format instructions
    const instructions = Array.isArray(recipeData.recipeInstructions)
      ? recipeData.recipeInstructions
          .map((step, idx) => {
            const text = typeof step === 'string' ? step : step.text || '';
            return `${idx + 1}. ${text}`;
          })
          .join('\n\n')
      : recipeData.recipeInstructions || '';

    // Create the recipe object
    const newRecipe = {
      title: recipeData.name || 'Untitled Recipe',
      meal: mealType,
      calories: calories,
      protein: protein,
      ingredients: ingredients,
      instructions: instructions,
      link: url,
      servings: recipeData.recipeYield || 1,
      prepTime: recipeData.prepTime || '',
      cookTime: recipeData.cookTime || ''
    };

    // Save to Firestore
    const recipeId = await addRecipe(newRecipe);
    
    console.log('NYT recipe imported successfully with ID:', recipeId);
    return recipeId;
    
  } catch (error) {
    console.error('Error importing NYT recipe:', error);
    throw error;
  }
};